import React, { useState } from 'react'
import type { PlanModulo } from '../tipos'
import styles from './ModalReportarAvance.module.css'

interface ModalReportarAvanceProps {
  abierto: boolean
  modulo: PlanModulo
  alCerrar: () => void
  alGuardarCorte: (datos: {
    cantidadCorte: number
    quienReporto: string
    fotoEvidenciaUrl?: string | null
    observaciones?: string
  }) => void
}

export function ModalReportarAvance({
  abierto,
  modulo,
  alCerrar,
  alGuardarCorte,
}: ModalReportarAvanceProps) {
  const contratada = Math.max(0.01, Number(modulo.cantidad_contratada || modulo.cantidad || 0))
  const acumuladaPrevia = Number(modulo.cantidad_ejecutada || 0)
  const unidad = modulo.unidad || 'UND'

  const [cantidadCorte, setCantidadCorte] = useState<string>('')
  const [quienReporto, setQuienReporto] = useState<string>('Ing. Residente')
  const [observaciones, setObservaciones] = useState<string>('')
  const [fotoBase64, setFotoBase64] = useState<string | null>(null)
  const [errorInput, setErrorInput] = useState<string | null>(null)

  if (!abierto) return null

  const cantCorteNum = parseFloat(cantidadCorte) || 0
  const nuevaAcumulada = Number((acumuladaPrevia + cantCorteNum).toFixed(2))
  const nuevoPctAvance = Math.min(100, Math.round((nuevaAcumulada / contratada) * 100))
  const saldoRestante = Math.max(0, Number((contratada - nuevaAcumulada).toFixed(2)))

  const handleFotoSeleccionada = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      alert('Por favor seleccione un archivo de imagen válido (JPG, PNG).')
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      setFotoBase64(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (cantCorteNum <= 0) {
      setErrorInput('La cantidad ejecutada en este corte debe ser mayor a 0.')
      return
    }

    if (nuevaAcumulada > contratada * 1.5) {
      if (!confirm(`La cantidad acumulada (${nuevaAcumulada} ${unidad}) supera significativamente lo contratado (${contratada} ${unidad}). ¿Desea registrar sobre-ejecución?`)) {
        return
      }
    }

    alGuardarCorte({
      cantidadCorte: cantCorteNum,
      quienReporto: quienReporto.trim() || 'Ing. Residente',
      fotoEvidenciaUrl: fotoBase64,
      observaciones: observaciones.trim(),
    })
    alCerrar()
  }

  return (
    <div className={styles.overlay} onClick={alCerrar}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <div className={styles.badgeTop}>CORTE DE MEDICIÓN EN OBRA</div>
            <h3 className={styles.titulo}>Reportar Avance Físico</h3>
            <p className={styles.subtitulo}>{modulo.nombre}</p>
          </div>
          <button type="button" className={styles.btnCerrar} onClick={alCerrar}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.cuerpoForm}>
          {/* Ficha métrica de la actividad */}
          <div className={styles.fichaMetricas}>
            <div className={styles.metricaBox}>
              <span className={styles.metricaLabel}>Contratado</span>
              <span className={styles.metricaValor}>
                {contratada.toLocaleString('es-CO')} <small>{unidad}</small>
              </span>
            </div>
            <div className={styles.metricaBox}>
              <span className={styles.metricaLabel}>Ejecutado Previo</span>
              <span className={styles.metricaValor}>
                {acumuladaPrevia.toLocaleString('es-CO')} <small>{unidad}</small>
              </span>
            </div>
            <div className={styles.metricaBox}>
              <span className={styles.metricaLabel}>Avance Actual</span>
              <span className={styles.metricaValor}>
                {Math.round(modulo.avance)}%
              </span>
            </div>
            <div className={`${styles.metricaBox} ${styles.metricaDestacada}`}>
              <span className={styles.metricaLabel}>Nuevo Avance Proyectado</span>
              <span className={styles.metricaValorNuevo}>
                {nuevoPctAvance}%
              </span>
            </div>
          </div>

          {/* Formulario de captura */}
          <div className={styles.camposGrid}>
            <div>
              <label className={styles.label}>
                Cantidad Ejecutada en este Corte ({unidad}) *
              </label>
              <input
                type="number"
                step="any"
                min="0.01"
                required
                placeholder="Ej: 45.5"
                value={cantidadCorte}
                onChange={(e) => {
                  setCantidadCorte(e.target.value)
                  setErrorInput(null)
                }}
                className={styles.inputNumero}
                autoFocus
              />
              <div className={styles.saldoTexto}>
                Acumulado resultante: <strong>{nuevaAcumulada} {unidad}</strong> • Saldo pendiente: <strong>{saldoRestante} {unidad}</strong>
              </div>
              {errorInput && <span className={styles.errorSpan}>{errorInput}</span>}
            </div>

            <div>
              <label className={styles.label}>Ing. Residente / Quien Reporta *</label>
              <input
                type="text"
                required
                placeholder="Nombre del residente o maestro..."
                value={quienReporto}
                onChange={(e) => setQuienReporto(e.target.value)}
                className={styles.inputTexto}
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label className={styles.label}>Observaciones / Bitácora de Campo</label>
              <textarea
                rows={2}
                placeholder="Condiciones climáticas, tramo fundido, personal activo, notas técnicas..."
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                className={styles.textarea}
              />
            </div>

            {/* Captura de Evidencia Fotográfica (Móvil / PC) */}
            <div style={{ gridColumn: '1 / -1' }} className={styles.seccionEvidencia}>
              <label className={styles.label}>
                Foto de Evidencia de Corte (Cámara Móvil o Archivo PC)
              </label>
              <div className={styles.uploadControles}>
                <label className={styles.btnTomarFoto}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  <span>Cámara / Seleccionar Foto</span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFotoSeleccionada}
                    className={styles.inputFileOculto}
                  />
                </label>
                {fotoBase64 && (
                  <button
                    type="button"
                    className={styles.btnQuitarFoto}
                    onClick={() => setFotoBase64(null)}
                  >
                    ✕ Quitar foto
                  </button>
                )}
              </div>

              {fotoBase64 && (
                <div className={styles.previewContenedor}>
                  <img src={fotoBase64} alt="Evidencia de corte" className={styles.imgPreview} />
                  <span className={styles.previewInfo}>Foto lista para sustento de acta de cobro</span>
                </div>
              )}
            </div>
          </div>

          <div className={styles.footer}>
            <button type="button" className={styles.btnCancelar} onClick={alCerrar}>
              Cancelar
            </button>
            <button type="submit" className={styles.btnGuardar}>
              Registrar Corte y Recalcular Avance
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

